import {Injectable} from '@angular/core';
import {HttpResponse} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';
import {BaseService} from '../../utils/base.service';

/**
 * Holds the access token in memory (never localStorage) so cross-host integrations that do NOT
 * receive the host-only auth cookie — the Node API v2, the AI API and the BA Dashboard SSO, all on
 * different hosts in staging/prod — can still authenticate with an Authorization: Bearer header.
 * The value is fetched from the cookie-protected /oauth/service-token endpoint on bootstrap and
 * after login.
 */
@Injectable({providedIn: 'root'})
export class ServiceTokenService {

  private token: string | null = null;
  private inFlight$: Observable<string | null> | null = null;

  constructor(private baseService: BaseService) {}

  get(): string | null {
    return this.token;
  }

  clear(): void {
    this.token = null;
  }

  /**
   * Returns a usable service token, fetching it on demand if it isn't in memory yet. Concurrent
   * callers share a single in-flight request so a burst of cross-host calls right after login (or on
   * first dashboard load) never races ahead of the token fetch.
   */
  ensureToken(): Observable<string | null> {
    if (this.token) {
      return of(this.token);
    }
    if (!this.inFlight$) {
      this.inFlight$ = this.refresh().pipe(
        finalize(() => { this.inFlight$ = null; }),
        shareReplay(1)
      );
    }
    return this.inFlight$;
  }

  /**
   * Discard the cached token and mint a new one. Called when a cross-host API has just rejected the
   * current token with 401 — the token is fetched once on bootstrap/login and carries no expiry we
   * can see, so a 401 from that host IS the expiry notice.
   *
   * Concurrent callers share the single in-flight fetch, so the burst of calls a page fires on open
   * (Pebbl sends `/pebbl/models` and `/pebbl/conversations` together) re-mints one token between
   * them rather than one each.
   */
  renew(): Observable<string | null> {
    this.token = null;
    if (!this.inFlight$) {
      this.inFlight$ = this.refresh().pipe(
        finalize(() => { this.inFlight$ = null; }),
        shareReplay(1)
      );
    }
    return this.inFlight$;
  }

  /** Fetch the token for cross-host calls. Resolves regardless of outcome (unauthenticated => null). */
  refresh(): Observable<string | null> {
    // skipLoader + skipError: this is a best-effort background fetch; failures are handled here.
    return this.baseService.get('auth/service-token', undefined, true, true).pipe(
      map((response: HttpResponse<any>) => (response?.body?.data?.token ?? null) as string | null),
      tap((token) => (this.token = token)),
      catchError(() => {
        this.token = null;
        return of(null);
      })
    );
  }
}
